import { Centrifuge, SubscriptionState } from "centrifuge";
import { acquireSubscription } from "@/features/chat/api/realtime";
import { getIceServers } from "@/features/rtc/iceServers";
import type { RtcLoggerOptions } from "@/features/rtc/rtcLogger";
import {
  guestDiagnosticsKeepalive,
  guestDiagnosticsSender,
  guestJoin,
  guestLeave,
  guestRtcTokens,
  guestSignal,
  joinOccurrence,
  leaveOccurrence,
  leaveOccurrenceKeepalive,
  sendRoomSignal,
} from "../api/meetingsApi";
import type { JoinContract, PeerId, RoomEvent, RoomSignalType } from "../types";

export interface RoomSignalMessage {
  to: PeerId | null;
  type: RoomSignalType;
  payload?: unknown;
}

/**
 * Abstracts how the room reaches the backend: an authenticated employee uses
 * the JWT API + the shared Centrifugo singleton; an external guest uses the
 * token-scoped `/meetings/guest/{token}/…` endpoints + a DEDICATED Centrifuge
 * connection built from the tokens minted in the guest join response (guests
 * cannot call the authenticated token endpoints).
 */
export interface RoomTransport {
  join(): Promise<JoinContract>;
  leave(): Promise<unknown> | void;
  /** pagehide-safe variant. */
  leaveKeepalive(): void;
  sendSignal(msg: RoomSignalMessage): Promise<unknown>;
  /**
   * `onResubscribed` fires on every re-subscribe AFTER the first (transport
   * came back, channel re-established) — the room uses it to resync the
   * roster, because joins/leaves published while we were away are gone
   * whenever Centrifugo recovery couldn't replay them.
   */
  subscribeRoom(
    contract: JoinContract,
    onEvent: (event: RoomEvent) => void,
    onResubscribed?: () => void,
  ): Promise<() => void>;
  /** Fresh ICE servers (short-lived TURN credentials) for late-created peer
   *  connections and ICE restarts. Returns null when unavailable. */
  freshIce(): Promise<RTCIceServer[] | null>;
  /** rtcLogger overrides (guests report through their own endpoint). */
  loggerOverrides(): Partial<RtcLoggerOptions>;
}

export function createUserTransport(occurrenceId: string): RoomTransport {
  // The join contract's session id — echoed on leave so a dying tab cannot
  // close a row its own reload already took over.
  let session: string | undefined;
  return {
    join: async () => {
      const contract = await joinOccurrence(occurrenceId);
      session = contract.session;
      return contract;
    },
    leave: () => leaveOccurrence(occurrenceId, session).catch(() => undefined),
    leaveKeepalive: () => leaveOccurrenceKeepalive(occurrenceId, session),
    sendSignal: (msg) => sendRoomSignal(occurrenceId, msg),
    subscribeRoom: async (contract, onEvent, onResubscribed) => {
      const handle = await acquireSubscription(contract.channel);
      const handler = (ctx: { data: unknown }) => onEvent(ctx.data as RoomEvent);
      handle.sub.on("publication", handler);
      // Only re-subscribes AFTER our attach mean we were away. The channel is
      // ref-counted and often ALREADY subscribed when we attach (the notes
      // thread holds it), in which case no "subscribed" event fires for us —
      // so seed from the current state instead of swallowing the first event,
      // which would silently skip the resync on the first real outage.
      let sawFirst = handle.sub.state === SubscriptionState.Subscribed;
      const subscribedHandler = () => {
        if (!sawFirst) {
          sawFirst = true;
          return;
        }
        onResubscribed?.();
      };
      handle.sub.on("subscribed", subscribedHandler);
      return () => {
        try {
          handle.sub.off("publication", handler);
          handle.sub.off("subscribed", subscribedHandler);
          handle.release();
        } catch {
          /* teardown */
        }
      };
    },
    freshIce: () => getIceServers().catch(() => null),
    loggerOverrides: () => ({}),
  };
}

export function createGuestTransport(token: string): RoomTransport {
  let session: string | undefined;
  return {
    join: async () => {
      const name = localStorage.getItem("elg5:guest-name") ?? "";
      const contract = await guestJoin(token, name);
      session = contract.session;
      return contract;
    },
    leave: () => guestLeave(token, session),
    leaveKeepalive: () => void guestLeave(token, session, true),
    sendSignal: (msg) => guestSignal(token, msg),
    subscribeRoom: async (contract, onEvent, onResubscribed) => {
      const rtc = contract.rtc;
      if (!rtc) throw new Error("Brak tokenów RTC w odpowiedzi joinu gościa.");
      const client = new Centrifuge(rtc.ws_url, {
        token: rtc.connection_token,
        getToken: async () => (await guestRtcTokens(token)).connection_token,
      });
      const sub = client.newSubscription(contract.channel, {
        token: rtc.subscription_token,
        getToken: async () => (await guestRtcTokens(token)).subscription_token,
      });
      const handler = (ctx: { data: unknown }) => onEvent(ctx.data as RoomEvent);
      sub.on("publication", handler);
      let sawFirst = sub.state === SubscriptionState.Subscribed;
      const subscribedHandler = () => {
        if (!sawFirst) {
          sawFirst = true;
          return;
        }
        onResubscribed?.();
      };
      sub.on("subscribed", subscribedHandler);
      sub.subscribe();
      client.connect();
      return () => {
        try {
          sub.off("publication", handler);
          sub.off("subscribed", subscribedHandler);
          sub.unsubscribe();
          client.disconnect();
        } catch {
          /* teardown */
        }
      };
    },
    freshIce: async () => {
      try {
        const rtc = await guestRtcTokens(token);
        return rtc.ice?.ice_servers ?? null;
      } catch {
        return null;
      }
    },
    loggerOverrides: () => ({
      send: guestDiagnosticsSender(token) as RtcLoggerOptions["send"],
      sendKeepalive: guestDiagnosticsKeepalive(token) as RtcLoggerOptions["sendKeepalive"],
    }),
  };
}

